import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Stack, Group, Button, Alert } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';

interface ConfirmMarkAsPaidModalProps {
  opened: boolean;
  onClose: () => void;
  markAsPaid: () => Promise<void>;
}

export function ConfirmMarkAsPaidModal({
  opened,
  onClose,
  markAsPaid,
}: ConfirmMarkAsPaidModalProps) {
  const { t } = useTranslation('lims');
  const [confirmPaidSaving, setConfirmPaidSaving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleConfirmMarkAsPaid = async () => {
    try {
      setConfirmPaidSaving(true);
      setLocalError(null);
      await markAsPaid();
      handleClose();
    } catch (e: any) {
      setLocalError(e.message || 'Failed to mark as paid');
    } finally {
      setConfirmPaidSaving(false);
    }
  };

  const handleClose = () => {
    setLocalError(null);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t('payment.markAsPaid')}
      centered
      size="sm"
    >
      <Stack gap="md">
        {localError && (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            {localError}
          </Alert>
        )}
        <Alert icon={<IconAlertCircle size={16} />} color="yellow">
          {t('payment.confirmMarkAsPaidMessage')}
        </Alert>
        <Group justify="flex-end">
          <Button variant="subtle" onClick={handleClose}>
            {t('payment.cancel')}
          </Button>
          <Button
            color="green"
            onClick={handleConfirmMarkAsPaid}
            loading={confirmPaidSaving}
          >
            {t('payment.confirm')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
